import React from 'react';

import Header from '@modules/common/components/Header';
import BaseModal from '@modules/common/components/BaseModal';
import Popup from '@src/components/common/Popup';
import { alertError, alertSuccess } from '@src/lib/alert';
import { ApiError } from '@src/lib/api';
import SentryUtils from '@src/lib/sentry';
import SecondaryButton from '@modules/common/components/buttons/SecondaryButton';
import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import { ModalContent } from '@modules/common/components/BaseModal/ModalContent';
import { ModalActions } from '@modules/common/components/BaseModal/ModalActions';
import { ModalHeader } from '@modules/common/components/BaseModal/ModalHeader';

import ClientResource from '../../../components/services/ClientResource';

type Props = {
  clientId: string;
  deleteClient: (clientId: string) => void;
};

const initialState = Object.freeze({
  deletingClient: false,
});

type State = typeof initialState;

class DeleteOauthClient extends React.PureComponent<Props> {
  readonly state: State = initialState;

  handleDelete = (handleClose: () => void) => () => {
    if (this.state.deletingClient) {
      return;
    }

    this.setState({ deletingClient: true });

    ClientResource.delete({ id: this.props.clientId })
      .then(() => this.handleOAuthClientDeleted(handleClose))
      .catch(this.handleDeletionError);
  };

  handleOAuthClientDeleted = (handleClose: () => void) => {
    this.setState({ deletingClient: false });
    alertSuccess('Client successfully deleted');
    this.props.deleteClient(this.props.clientId);
    handleClose();
  };

  handleDeletionError = (apiError: ApiError<void>) => {
    const errorMessage = 'Something went wrong while deleting client';
    alertError(errorMessage);
    this.setState({ deletingClient: false });
    SentryUtils.captureAPIError(apiError, errorMessage);
  };

  render() {
    return (
      <BaseModal
        trigger={handleOpen => (
          <Popup
            trigger={
              <SecondaryButton
                icon="trash"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleOpen();
                }}
              />
            }
            content="Delete OAuth client"
          />
        )}
      >
        {handleClose => (
          <>
            <ModalHeader>
              <Header content="Delete OAuth client" icon="trash" />
            </ModalHeader>
            <ModalContent>
              Are you sure you want to delete OAuth client:{' '}
              <b>
                <code>{this.props.clientId}</code>
              </b>{' '}
              ?
            </ModalContent>
            <ModalActions>
              <SecondaryButton content="Cancel" onClick={handleClose} icon="remove" />
              <PrimaryButton
                onClick={this.handleDelete(handleClose)}
                icon="trash"
                content="Delete client"
              />
            </ModalActions>
          </>
        )}
      </BaseModal>
    );
  }
}

export default DeleteOauthClient;
